import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { saveDownload } from "@/lib/reporting";
import { canRevokeCertificates, canViewAudit } from "@/lib/roles";
import {
  Copy,
  DownloadSimple,
  Prohibit,
  SealCheck,
  ClockCounterClockwise,
  ListMagnifyingGlass,
} from "@phosphor-icons/react";

const STATE_STYLES = {
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  superseded: "bg-amber-50 text-amber-700 border-amber-200",
  revoked: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATE_ICONS = {
  verified: SealCheck,
  superseded: ClockCounterClockwise,
  revoked: Prohibit,
};

/**
 * The verification certificate for a released sample (FR-18).
 *
 * Only rendered for released samples — an unreleased sample has no published
 * document to attest to, so there is nothing to show rather than an empty
 * state to explain.
 *
 * The QR is fetched as a blob so authorization and network failures stay in
 * the component's explicit error path. Authentication is the HttpOnly
 * same-origin session cookie.
 */
export default function CertificatePanel({ sampleId, user }) {
  const [data, setData] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: body } = await api.get(`/samples/${sampleId}/certificate`);
      setData(body);
      setMissing(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setMissing(true);
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, [sampleId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data) return undefined;
    let revoked = null;
    api
      .get(`/samples/${sampleId}/certificate/qr.png`, { responseType: "blob" })
      .then(({ data: blob }) => {
        revoked = URL.createObjectURL(blob);
        setQrUrl(revoked);
      })
      .catch(() => setQrUrl(null));
    // Object URLs live until revoked; without this each poll would leak one.
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [data, sampleId]);

  const certificate = data?.certificate;

  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(certificate.verify_url);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const downloadQr = async (format) => {
    try {
      const { data: blob } = await api.get(
        `/samples/${sampleId}/certificate/qr.${format}`,
        { responseType: "blob" }
      );
      saveDownload(
        blob,
        `${certificate.certificate_code}.${format}`,
        format === "png" ? "image/png" : "image/svg+xml"
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const loadAttempts = async () => {
    try {
      const { data: body } = await api.get(
        `/samples/${sampleId}/certificate/attempts`
      );
      setAttempts(body.attempts || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const revoke = async () => {
    const reason = window.prompt(
      "Revoke this certificate. Anyone scanning the printed QR will be told it " +
        "was withdrawn.\nReason (required):"
    );
    if (reason == null) return;
    if (!reason.trim()) {
      toast.error("Revoking a certificate requires a reason");
      return;
    }
    try {
      await api.post(`/certificates/${certificate.id}/revoke`, { reason });
      toast.success("Certificate revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (missing || !certificate) return null;

  const StateIcon = STATE_ICONS[certificate.state] || SealCheck;

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-6"
      data-testid="certificate-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">
          Certificate verification
        </h2>
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
            STATE_STYLES[certificate.state] || STATE_STYLES.verified
          }`}
          data-testid="certificate-state"
        >
          <StateIcon size={12} weight="fill" />
          {certificate.state}
        </span>
      </div>

      <div className="flex gap-5">
        {qrUrl && (
          <img
            src={qrUrl}
            alt={`QR code linking to the public verification page for ${certificate.certificate_code}`}
            className="w-28 h-28 shrink-0 rounded border border-slate-200"
            data-testid="certificate-qr"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-500">Certificate</p>
          <p className="text-sm text-slate-900">
            {certificate.certificate_code}{" "}
            <span className="text-slate-400">v{certificate.version}</span>
          </p>
          <p className="text-xs text-slate-500 mt-3">Verification link</p>
          <div className="flex items-center gap-2 mt-0.5">
            <code className="text-xs text-slate-700 truncate" data-testid="certificate-url">
              {certificate.verify_url}
            </code>
            <button
              type="button"
              onClick={copyLink}
              title="Copy verification link"
              className="shrink-0 text-slate-400 hover:text-slate-900 transition-colors"
              data-testid="certificate-copy"
            >
              <Copy size={16} />
            </button>
          </div>
          {certificate.revoked_at && (
            <p className="text-xs text-rose-600 mt-3">
              Revoked by {certificate.revoked_by} on{" "}
              {new Date(certificate.revoked_at).toLocaleString()}
              {certificate.revoke_reason ? `: ${certificate.revoke_reason}` : ""}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        <button
          type="button"
          onClick={() => downloadQr("png")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="certificate-download-png"
        >
          <DownloadSimple size={14} /> QR as PNG
        </button>
        <button
          type="button"
          onClick={() => downloadQr("svg")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="certificate-download-svg"
        >
          <DownloadSimple size={14} /> QR as SVG
        </button>
        {canViewAudit(user) && (
          <button
            type="button"
            onClick={loadAttempts}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
            data-testid="certificate-attempts"
          >
            <ListMagnifyingGlass size={14} /> Verification log
          </button>
        )}
        {canRevokeCertificates(user) && !certificate.revoked_at && (
          <button
            type="button"
            onClick={revoke}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-status-critical border border-status-critical/30 hover:bg-status-critical/10 transition-colors"
            data-testid="certificate-revoke"
          >
            <Prohibit size={14} /> Revoke
          </button>
        )}
      </div>

      {attempts && (
        <div className="mt-4 border-t border-slate-100 pt-4" data-testid="certificate-attempt-list">
          {attempts.length === 0 ? (
            <p className="text-xs text-slate-400">
              Nobody has checked this certificate yet.
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {attempts.map((row, index) => (
                <li key={index} className="text-xs text-slate-600">
                  <time dateTime={row.timestamp} data-timestamp>
                    {new Date(row.timestamp).toLocaleString()}
                  </time>{" "}
                  — {row.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {data.history.length > 1 && (
        <p className="text-xs text-slate-400 mt-4">
          {data.history.length} versions issued for this sample. Earlier tokens
          stay resolvable and point at the current version.
        </p>
      )}
    </div>
  );
}
